// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { eq, flow, get, values, without } from 'lodash/fp';
import { branch, compose, withProps } from 'recompose';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';

import Page from 'app/containers/parts/Page';
import t from 'app/utils/translate';
import MyChallengesInfoModal from 'app/components/challenge/MyChallengesInfoModal';
import ChallengesList from 'app/components/challenges/ChallengesList';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import MyChallengesMenu from 'app/components/challenges/MyChallengesMenu';
import SportsFilterBox from 'app/components/sportsFilters/SportsFilterBox';
import UserOnly from 'app/components/util/UserOnly';
import { filterByPropValues } from 'app/utils/functionalUtils';
import withSort from 'app/hoc/withSort';
import { ASCENDING, DESCENDING } from 'app/constants/sortDirTypes';
import {
  ENTRY_COUNT,
  EXPAND,
  FEE,
  POINTS,
  POSITION,
  PRIZE,
  WINNINGS,
  WON
} from 'app/constants/columnTypes';
import Button from '../components/commons/Button';
import Icon from '../components/commons/Icon';
import NotificationHandler from '../components/notifications/NotificationHandler';
import { rewritePath } from '../utils/routeUtils';
import * as lobbyActions from '../actions/lobbyActions';
import * as myChallengesActions from '../actions/myChallengesActions';

type Props = {
  activeSection: 'completed' | 'live' | 'upcoming',
  activeSport?: string,
  challenges: Array<*>,
  columns: Array<*>,
  deleteChallenges: (ids: Array<string>) => void,
  emptyMessage: string,
  emptyTitle: string,
  enableDelete?: boolean,
  errorMessages: Array<Object>,
  expandType: 'edit' | 'summary' | 'view',
  isDeletingChallenges: boolean,
  isFetching: boolean,
  onSort: (?string) => void,
  rewriteRules: Object,
  setLobbySport: (sport: string, subCategory?: string) => void,
  setLobbySubCategory: (subCategory: string) => void,
  sortDirection: ASCENDING | DESCENDING,
  sortField?: string,
  startChallengesPoll: () => void,
  stopChallengesPoll: () => void,
  successMessage: ?string,
  title: string
};

type State = {
  detailedChallengeId: string,
  expandedChallengeId: string
};

const MenuWrapper = styled.div({
  paddingBottom: '1rem'
});

const Main = styled.div({
  position: 'relative'
});

const SmallButton = styled(Button)(
  {
    minHeight: 51,
    maxWidth: 200,
    margin: '0 auto'
  },
  ({ theme }) => ({
    color: theme.color.basis4,
    marginTop: theme.spacing.small
  })
);

class MyChallenges extends React.Component<Props, State> {
  props: Props;

  state: State = {
    expandedChallengeId: undefined,
    selectedIds: []
  };

  static defaultProps = {
    activeSport: undefined,
    sortField: undefined,
    enableDelete: false
  };

  componentDidMount() {
    const { startChallengesPoll } = this.props;

    startChallengesPoll(true);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { isDeletingChallenges } = this.props;

    if (
      isDeletingChallenges &&
      !nextProps.isDeletingChallenges &&
      nextProps.errorMessages.length === 0
    ) {
      this.setState({ selectedIds: [] });
    }
  }

  componentWillUnmount() {
    const { stopChallengesPoll } = this.props;

    stopChallengesPoll();
  }

  handleExpand = (id: string) => {
    this.setState(state => ({
      expandedChallengeId: id !== state.expandedChallengeId ? id : undefined
    }));
  };

  openChallengeModal = (id: string) => {
    this.setState({
      detailedChallengeId: id
    });
  };

  closeChallengeModal = () => {
    this.setState({
      detailedChallengeId: undefined
    });
  };

  handleDeleteClick = () => {
    this.setState(state => ({ showDelete: !state.showDelete }));
  };

  handleCancelClick = () => {
    this.setState({ selectedIds: [] });
  };

  handleCheckBoxClick = id => {
    this.setState(state => ({
      selectedIds: state.selectedIds.includes(id)
        ? without([id], state.selectedIds)
        : [...state.selectedIds, id]
    }));
  };

  handleConfirmClick = () => {
    const { deleteChallenges } = this.props;
    const { selectedIds } = this.state;

    deleteChallenges(selectedIds);
  };

  render() {
    const {
      activeSection,
      activeSport,
      challenges,
      columns,
      emptyMessage,
      emptyTitle,
      enableDelete,
      errorMessages,
      expandType,
      isDeletingChallenges,
      isFetching,
      onSort,
      rewriteRules,
      setLobbySport,
      setLobbySubCategory,
      sortDirection,
      sortField,
      successMessage,
      title
    } = this.props;
    const { detailedChallengeId, expandedChallengeId, selectedIds, showDelete } = this.state;

    const rewrite = rewritePath(rewriteRules);

    return (
      <UserOnly>
        <Page title={t('My Challenges')}>
          <Helmet title={t('My Challenges')} />
          <MenuWrapper>
            <SportsFilterBox
              activeSport={activeSport}
              namespace={`${rewrite('/my-challenges')}/${activeSection}`}
              onSportSelect={setLobbySport}
              onSubCategorySelect={setLobbySubCategory}
              showSubCategories={false}
              showAll
            />
            <MyChallengesMenu active={activeSection} rest={activeSport} />
          </MenuWrapper>
          <Main>
            {isFetching && <FriendsLoader showOverlay>{t('Fetching challenges...')}</FriendsLoader>}
            <ChallengesList
              challenges={challenges}
              columns={columns}
              emptyMessage={emptyMessage}
              emptyTitle={emptyTitle}
              enableDelete={enableDelete}
              expandedChallengeId={expandedChallengeId}
              expandType={expandType}
              isDeletingChallenges={isDeletingChallenges}
              onCancelClick={this.handleCancelClick}
              onCheckBoxClick={this.handleCheckBoxClick}
              onConfirmClick={this.handleConfirmClick}
              onDeleteClick={this.handleDeleteClick}
              onExpand={this.handleExpand}
              onNameClick={this.openChallengeModal}
              onSort={onSort}
              selectedIds={selectedIds}
              showDelete={showDelete}
              showPlaceholder
              sortDirection={sortDirection}
              sortField={sortField}
              title={title}
            />
            {activeSection === 'completed' && (
              <SmallButton size="medium" kind="bare" link="/account/history" block>
                <Icon type="icon_menu_history" /> Full History
              </SmallButton>
            )}
          </Main>
          {detailedChallengeId && (
            <MyChallengesInfoModal
              activeSection={activeSection}
              challengeId={detailedChallengeId}
              onClose={this.closeChallengeModal}
            />
          )}
          <NotificationHandler
            type="success"
            afterCond={!isDeletingChallenges && !!successMessage}
            beforeCond={isDeletingChallenges}
          >
            {successMessage}
          </NotificationHandler>
          <NotificationHandler
            type="danger"
            afterCond={!isDeletingChallenges && errorMessages.length > 0}
            beforeCond={isDeletingChallenges}
          >
            {errorMessages[0]}
          </NotificationHandler>
        </Page>
      </UserOnly>
    );
  }
}

const connector = connect(
  (state, { detailedChallengeId, match, sortData, sortDescending }) => {
    const { section, sport } = match.params;
    const sportObj = state.sportCategories.items[sport];
    const sortAndFilterData = flow(
      filterByPropValues(get('filterKey', sportObj), get('filterValues', sportObj)),
      sortData
    );
    const challengesMap = get('challenges', state.myChallenges[section]);

    return {
      activeSection: section,
      activeSport: state.lobbyChallenges.activeSport,
      challenges: sortAndFilterData(values(challengesMap)),
      detailedChallenge: challengesMap[detailedChallengeId],
      displayCreateH2H: state.settings.displayCreateH2H,
      errorMessages: get('errorMessages', state.myChallenges[section]),
      isDeletingChallenges: state.myChallenges.isDeletingChallenges,
      isFetching: get('isFetching', state.myChallenges[section]),
      rewriteRules: state.settings.rewriteRules,
      sortDirection: sortDescending ? DESCENDING : ASCENDING,
      successMessage: state.myChallenges.successMessage
    };
  },
  {
    startChallengesPoll: myChallengesActions.startMyChallengesPoll,
    stopChallengesPoll: myChallengesActions.stopMyChallengesPoll,
    setLobbySport: lobbyActions.setLobbySport,
    setLobbySubCategory: lobbyActions.setLobbySubCategory,
    deleteChallenges: myChallengesActions.deleteChallenges
  }
);

const liveColumns = [POSITION, POINTS, WINNINGS, EXPAND];
const upcomingColumns = [FEE, ENTRY_COUNT, PRIZE, EXPAND];
const completedColumns = [POSITION, POINTS, WON, EXPAND];

const addProps = withProps(
  ({ activeSection, displayCreateH2H }) =>
    ({
      completed: {
        columns: completedColumns,
        emptyMessage: t('The recently completed challenges that you entered will appear here'),
        emptyTitle: t('No recently completed challenges'),
        expandType: 'summary',
        title: t('Completed challenges')
      },
      live: {
        columns: liveColumns,
        emptyMessage: t('The live challenges that you entered will appear here'),
        emptyTitle: t('No live challenges'),
        expandType: 'view',
        title: t('Live challenges')
      },
      upcoming: {
        columns: upcomingColumns,
        emptyMessage: t('The live challenges that you entered will appear here'),
        emptyTitle: t('No upcoming challenges'),
        expandType: 'edit',
        title: t('Upcoming challenges'),
        enableDelete: displayCreateH2H
      }
    }[activeSection])
);

const enhance = compose(
  branch(
    flow(get('match.params.section'), eq('completed')),
    withSort({
      initialSortField: 'startDate',
      initialSortDescending: true
    }),
    withSort({
      initialSortField: 'startDate',
      initialSortDescending: false
    })
  ),
  connector,
  addProps
);

export default enhance(MyChallenges);
